import { createReducer } from '@reduxjs/toolkit';
import { AppActions } from './actions';
import Constants from './constants';
import { OrganizationActions } from '../organization/actions';
import { ProfileActions } from '../profile/actions';
import { BillingActions } from '../billing/actions';
import { UsersActions } from '../users/actions';

const trackingFromServer = window.__INITIAL_STATE.tracking || '';
const userFromServer = window.__INITIAL_STATE.user || '';
const orgFromServer = window.__INITIAL_STATE.organization || '';
const permissionsArrayFromServer = window.__INITIAL_STATE.permissionsArray || [];
const permittedAppsFromServer = window.__INITIAL_STATE.permittedApps || [];
const shouldDisplayPasswordExpireBanner = window.__INITIAL_STATE.should_display_password_expire_banner || false;
const hasApiContext = window.location.pathname.includes('/api/');

const initialState = {
  useCasesOptions: [],
  isLoading: 0,
  isLogged: window.__INITIAL_STATE?.is_logged || false,
  isOrgAdmin: window.__INITIAL_STATE?.is_org_admin || false,

  onBoarding: {
    blockReason: null,
    confirmEmail: window.__INITIAL_STATE.confirmEmail || '',
    currentEmail: window.__INITIAL_STATE.currentEmail || '',
    isOnBoardingVerifyAPI: window.__INITIAL_STATE.isOnBoardingVerifyAPI || false,
  },
  user: {
    firstName: userFromServer.first_name || '',
    lastName: userFromServer.last_name || '',
    isActiveOrg: orgFromServer.is_active || false,
    orgId: orgFromServer.org_id || null,
    orgName: orgFromServer.name || null,
    userId: userFromServer.user_id || null,
    email: userFromServer.email || '',
    username: userFromServer.username || '',
    teams: userFromServer.teams || [],
    passwordExpiration: userFromServer.password_expiration || null,
    shouldDisplayPasswordExpireBanner,
  },
  organization: {
    orgId: orgFromServer.org_id || null,
    isTeamManagement: orgFromServer.is_team_management || false,
    paymentMethod: orgFromServer.payment_method || {},
    signupPaymentMethod: orgFromServer.token_payment_method || null,
  },
  tracking: {
    googleAnalyticsId: trackingFromServer.google_analytics_id || '',
    isEnabled: trackingFromServer.is_enabled || '',
    isProfilePopupTimeEventEnabled: trackingFromServer.is_profile_popup_time_event_enabled,
    sendDimensions: trackingFromServer.send_dimensions,
    userCountry: trackingFromServer.user_country || '',
    userIp: trackingFromServer.user_ip || '',
    userAgent: trackingFromServer.user_useragent || '',
    screenHeight: trackingFromServer.screen_resolution_height || 0,
    screenWidth: trackingFromServer.screen_resolution_width || 0,
    deviceType: trackingFromServer.device_type || '',
    idUtPipl: trackingFromServer.id_ut_pipl || '',
  },
  permissionsArray: permissionsArrayFromServer,
  permittedApps: permittedAppsFromServer,
  hasBanner: false,
  hasApiContext,
};

const actionsLoaderNegative = [
  OrganizationActions.editDetailsHandler,
  OrganizationActions.editOrgDetailsHandler,
  OrganizationActions.forceGoogleLoginDisable,
  OrganizationActions.forceGoogleLoginEnable,

  ProfileActions.changeProfileDetails,
  ProfileActions.changePassword,

  BillingActions.addCreditCard,

  UsersActions.resendInvite,
  UsersActions.removeInvite,

  UsersActions.resetPassword,
  UsersActions.changeUserStatus,
  UsersActions.editProfile,
  UsersActions.removeUser,
  UsersActions.createUser,
  UsersActions.createTeam,
  UsersActions.deleteTeam,
  UsersActions.renameTeam,
  UsersActions.bulkAssignToTeams,
  UsersActions.removeUsers,
  UsersActions.inviteUser,
];

const actionsLoaderPositive = [
  OrganizationActions.forceGoogleLoginDisableFailed,
  OrganizationActions.forceGoogleLoginDisableSucceeded,

  OrganizationActions.forceGoogleLoginEnableFailed,
  OrganizationActions.forceGoogleLoginEnableSucceeded,

  OrganizationActions.editDetailsHandlerSucceeded,
  OrganizationActions.editDetailsHandlerFailed,

  ProfileActions.changeProfileDetailsSucceeded,
  ProfileActions.changeProfileDetailsFailed,

  ProfileActions.changePasswordSucceeded,
  ProfileActions.changePasswordFailed,

  UsersActions.resendInviteFailed,
  UsersActions.resendInviteSucceeded,

  UsersActions.removeInviteFailed,
  UsersActions.removeInviteSucceeded,

  UsersActions.changeUserStatusSucceeded,
  UsersActions.changeUserStatusFailed,
  UsersActions.editProfileSucceeded,
  UsersActions.editProfileFailed,
  UsersActions.removeUserSucceeded,
  UsersActions.removeUserFailed,
  UsersActions.createUserSucceeded,
  UsersActions.createUserFailed,

  UsersActions.inviteUserSucceeded,
  UsersActions.inviteUserFailed,

  UsersActions.resetPasswordFailed,
  UsersActions.resetPasswordSucceeded,

  UsersActions.createTeamSucceeded,
  UsersActions.createTeamFailed,

  UsersActions.deleteTeamSucceeded,
  UsersActions.deleteTeamFailed,

  UsersActions.renameTeamSucceeded,
  UsersActions.renameTeamFailed,

  UsersActions.bulkAssignToTeamsSucceeded,
  UsersActions.bulkAssignToTeamsFailed,

  UsersActions.removeUsersSucceeded,
  UsersActions.removeUsersFailed,

  BillingActions.addCreditCardSucceeded,
  BillingActions.addCreditCardFailed,
];

const ACTION_HANDLER = {
  [AppActions.setOnBoardingBlockReason]: (state, action) => {
    state.onBoarding.blockReason = action.payload;
  },
  [AppActions.setOnBoardingEmailConfirm]: (state, action) => {
    state.onBoarding.confirmEmail = action.payload;
  },
  [AppActions.fetchUseCasesSucceeded]: (state, action) => {
    state.useCasesOptions = action.payload;
  },
  [AppActions.enableTeamManagementSucceeded]: (state) => {
    state.organization.isTeamManagement = true;
  },
  [AppActions.disableTeamManagementSucceeded]: (state) => {
    state.organization.isTeamManagement = false;
  },
  [AppActions.closedPasswordBanner]: (state) => {
    state.user.shouldDisplayPasswordExpireBanner = false;
  },
  [AppActions.updateBannerState]: (state, action) => {
    state.hasBanner = action.payload;
  },
  ...actionsLoaderNegative.reduce((acc, n) => {
    acc[n] = (state) => {
      state.isLoading -= 1;
    };

    return acc;
  }, {}),
  ...actionsLoaderPositive.reduce((acc, n) => {
    acc[n] = (state) => {
      state.isLoading += 1;
    };

    return acc;
  }, {}),
};

export const appReducer = createReducer(initialState, ACTION_HANDLER);

export default {
  name: Constants.MODULE_NAME,
  reducer: appReducer,
};
